import { useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { desksEndpoints } from '@/api/endpoints';
import { Badge } from '@/components/ui/badge';
import { CommandGroup } from '@/components/ui/command';
import { type Desks } from '@/hooks/useDesks';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CommandLinkItem } from '../CommandLinkItem';
import { useVisiblePages } from '../hooks/useVisiblePages';
import { findBestPageMatch } from '../pageRegistry';
import { type RecentPage } from '../types';
import { extractDeskId } from '../utils';

type RecentPagesGroupProps = {
  recentPages: RecentPage[];
  onSelect: (path: string, label: string, opts?: { fullPath?: boolean }) => void;
};

export const RecentPagesGroup = ({ recentPages, onSelect }: RecentPagesGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const visiblePages = useVisiblePages();
  const queryClient = useQueryClient();

  // Recent pages replay any previously visited path — drop ones the user is no longer permitted to see.
  const visibleRecentPages = useMemo(
    () =>
      recentPages
        .map((page) => ({ page, match: findBestPageMatch(page.path) }))
        .filter(({ match }) => !match || visiblePages.includes(match)),
    [recentPages, visiblePages]
  );

  if (visibleRecentPages.length === 0) {
    return null;
  }

  const desksData = queryClient.getQueryData<Desks>([desksEndpoints.desks]);
  const deskMap = new Map(desksData?.desks?.map((d) => [d.desk_id, d.name]));

  return (
    <CommandGroup heading={t('command_palette.recent')}>
      {visibleRecentPages.map(({ page, match }) => {
        const Icon = match?.icon ?? ClockIcon;
        const deskId = extractDeskId(page.path);
        const deskName = deskId ? deskMap.get(deskId) : undefined;
        return (
          <CommandLinkItem
            key={page.path}
            value={`recent-${page.path}`}
            href={match?.fullPath ? page.path : slug ? `/${slug}${page.path}` : '#'}
            onSelect={() =>
              onSelect(page.path, page.label, match?.fullPath ? { fullPath: true } : undefined)
            }
            keywords={[page.label]}
          >
            <Icon />
            <span className="flex-1 truncate">{page.label}</span>
            {deskName && (
              <Badge type="outline" flavour="neutral" className="ml-2 shrink-0 text-xs font-normal">
                {deskName}
              </Badge>
            )}
          </CommandLinkItem>
        );
      })}
    </CommandGroup>
  );
};
